import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  MessagesSquare,
  Search,
  AlertCircle,
  ThumbsDown,
  AlertTriangle,
  RotateCw,
  Inbox,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import {
  listConversations,
  type ConversationListItem,
  type ConversationFilter,
  type ConversationsListPayload,
} from "@/lib/api";
import { relative, absolute } from "@/lib/format";
import { cn } from "@/lib/cn";
import { ConversationDetail } from "./ConversationDetail";

const PAGE_SIZE = 25;

/**
 * Conversations page. Master/detail layout:
 *   - Left: filterable list with search + pagination.
 *   - Right: full thread + citations + telemetry for the selected one.
 *
 * Selection is reflected in the URL (`?id=<uuid>`) so deep-linking from
 * the Home recent-conversations card works.
 */
export function Conversations() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("id");

  const [filter, setFilter] = useState<ConversationFilter>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [offset, setOffset] = useState(0);

  const [data, setData] = useState<ConversationsListPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Debounce the search input by 250ms so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Any filter or search change resets pagination.
  useEffect(() => { setOffset(0); }, [filter, debouncedSearch]);

  const fetchPage = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await listConversations({
        limit: PAGE_SIZE,
        offset,
        filter,
        search: debouncedSearch,
      });
      setData(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [offset, filter, debouncedSearch]);

  useEffect(() => { void fetchPage(); }, [fetchPage]);

  // Once the first page loads, auto-select the first conversation unless
  // the URL already targets one — this gives the page a useful initial
  // state instead of an empty detail panel.
  const autoSelected = useRef(false);
  useEffect(() => {
    if (autoSelected.current) return;
    if (!data || data.items.length === 0) return;
    if (!selectedId) {
      setParams({ id: data.items[0].id }, { replace: true });
    }
    autoSelected.current = true;
  }, [data, selectedId, setParams]);

  const selectConv = (id: string) => {
    setParams({ id }, { replace: true });
  };

  const total = data?.total ?? 0;
  const showingFrom = data && data.items.length > 0 ? offset + 1 : 0;
  const showingTo   = data ? offset + data.items.length : 0;
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < total;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-medium text-text-strong">Conversations</h1>
          <p className="mt-1 text-sm text-text-soft">
            Browse every chat your widget has handled. Filter to the ones worth reviewing — negative feedback or answers without grounding.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void fetchPage()}
          loading={loading}
          iconLeft={!loading && <RotateCw size={12} strokeWidth={1.75} />}
        >
          Refresh
        </Button>
      </div>

      {/* Filters + search */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 border-b border-line">
          <FilterTab active={filter === "all"} onClick={() => setFilter("all")}>
            All
          </FilterTab>
          <FilterTab active={filter === "negative"} onClick={() => setFilter("negative")}>
            <ThumbsDown size={12} strokeWidth={1.75} className="text-danger" />
            Negative feedback
          </FilterTab>
          <FilterTab active={filter === "no_retrieval"} onClick={() => setFilter("no_retrieval")}>
            <AlertTriangle size={12} strokeWidth={1.75} className="text-warning" />
            No retrieval
          </FilterTab>
        </div>

        <label className="relative inline-flex items-center">
          <Search
            size={14}
            strokeWidth={1.75}
            className="pointer-events-none absolute left-2.5 text-text-faint"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search messages…"
            className="h-8 w-64 rounded border border-line bg-surface pl-8 pr-3 text-xs text-text-strong outline-none focus:border-coral"
          />
        </label>
      </div>

      {/* Master/detail grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(280px,360px)_1fr]">
        <Card className="flex max-h-[calc(100vh-260px)] flex-col overflow-hidden">
          {error && (
            <div className="flex items-start gap-3 px-5 py-4">
              <AlertCircle size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-danger" />
              <div className="flex-1">
                <p className="text-sm font-medium text-text-strong">Couldn't load</p>
                <p className="mt-1 text-xs text-text-soft">{error}</p>
              </div>
            </div>
          )}

          {!error && loading && !data && <ListSkeleton />}

          {!error && data && data.items.length === 0 && (
            <div className="px-5 py-6">
              <EmptyState
                icon={debouncedSearch || filter !== "all" ? Inbox : MessagesSquare}
                title={debouncedSearch || filter !== "all" ? "No matches" : "No conversations yet"}
                description={
                  debouncedSearch || filter !== "all"
                    ? "Try removing the filter or search term."
                    : "When visitors chat with your widget, sessions will show up here."
                }
              />
            </div>
          )}

          {!error && data && data.items.length > 0 && (
            <>
              <ul className="flex-1 divide-y divide-line overflow-y-auto scrollbar-thin">
                {data.items.map((c) => (
                  <ConversationListRow
                    key={c.id}
                    item={c}
                    active={c.id === selectedId}
                    onSelect={() => selectConv(c.id)}
                  />
                ))}
              </ul>

              <div className="flex items-center justify-between gap-2 border-t border-line px-3 py-2">
                <span className="text-2xs tabular-nums text-text-soft">
                  {showingFrom}–{showingTo} of {total}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!hasPrev || loading}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={14} strokeWidth={1.75} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!hasNext || loading}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                    aria-label="Next page"
                  >
                    <ChevronRight size={14} strokeWidth={1.75} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>

        <ConversationDetail conversationId={selectedId} />
      </div>
    </div>
  );
}

function FilterTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs transition-colors",
        active
          ? "border-coral text-text-strong"
          : "border-transparent text-text-soft hover:text-text-strong",
      )}
    >
      {children}
    </button>
  );
}

function ConversationListRow({
  item,
  active,
  onSelect,
}: {
  item: ConversationListItem;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          "block w-full px-4 py-3 text-left transition-colors",
          active ? "bg-coral-soft" : "hover:bg-surface-2",
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <p
            className="line-clamp-2 flex-1 text-sm font-medium text-text-strong"
            title={item.first_user_message ?? ""}
          >
            {item.first_user_message ?? "(no question recorded)"}
          </p>
          <div className="flex shrink-0 items-center gap-1">
            {item.has_negative_feedback && (
              <ThumbsDown
                size={12}
                strokeWidth={1.75}
                className="text-danger"
                aria-label="Negative feedback"
              />
            )}
            {item.has_no_retrieval && (
              <AlertTriangle
                size={12}
                strokeWidth={1.75}
                className="text-warning"
                aria-label="No retrieval"
              />
            )}
          </div>
        </div>
        {item.last_assistant_message && (
          <p className="mt-1 line-clamp-1 text-xs text-text-soft">
            {item.last_assistant_message}
          </p>
        )}
        <p className="mt-1 text-2xs text-text-faint">
          <span title={absolute(item.last_message_at)} className="cursor-default">
            {relative(item.last_message_at)}
          </span>
          <span className="mx-1.5">·</span>
          <span className="tabular-nums">{item.message_count}</span>{" "}
          {item.message_count === 1 ? "msg" : "msgs"}
        </p>
      </button>
    </li>
  );
}

function ListSkeleton() {
  return (
    <ul className="divide-y divide-line">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <li key={i} className="px-4 py-3">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="mt-2 h-3 w-2/3" />
          <Skeleton className="mt-2 h-3 w-1/3" />
        </li>
      ))}
    </ul>
  );
}
